import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmModal,
  DataTable,
  Toast,
} from '../ui';
import Drawer from './Drawer';
import {
  listAttributes,
  listGroups,
  listFieldTypes,
  listLocales,
  getAttribute,
  getAttributeSourceValues,
  createAttribute,
  updateAttribute,
  deleteAttribute,
  replaceAttributeOptions,
} from '../../services/pimApi';

const OPTION_TYPES = new Set(['simpleselect', 'multiselect']);

// Settings keys meaningful per type — mirrors TypeSettings' rendered inputs so a
// type change can drop stale keys for clean UX. The backend filter is the
// authoritative source of truth; this is purely cosmetic.
const SETTINGS_KEYS = {
  text: ['max_length', 'pattern'],
  textarea: ['max_length', 'pattern'],
  rich_text: ['max_length', 'pattern'],
  number: ['min', 'max'],
  currency: ['currencies'],
  metric: ['unit_family', 'units', 'default_unit'],
  image_carousel: ['max_items'],
  simpleselect: ['allow_arbitrary'],
  multiselect: ['allow_arbitrary'],
};

const pruneSettings = (settings, type) => {
  const allowed = SETTINGS_KEYS[type] || [];
  return Object.fromEntries(
    Object.entries(settings || {}).filter(([k]) => allowed.includes(k)),
  );
};

// Derive a PIM option code from a raw source value: lowercase, non-alphanumeric
// runs collapse to "_", trimmed. Falls back to "option" if nothing remains.
const slugifyCode = (value) => {
  const slug = String(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return slug || 'option';
};

// Humanize a raw source value into a display label: separators to spaces, title-case.
const humanizeLabel = (value) =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const blankForm = () => ({
  code: '',
  label: '',
  type: 'text',
  group_code: '',
  is_localizable: false,
  is_scopable: false,
  is_required_default: false,
  settings: {},
  options: [],
});

export default function AttributesPanel() {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [fieldTypes, setFieldTypes] = useState([]);
  const [locales, setLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null); // form object or null
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, g, ft, loc] = await Promise.all([
        listAttributes(), listGroups(), listFieldTypes(), listLocales(),
      ]);
      setRows(a);
      setGroups(g);
      setFieldTypes(ft);
      setLocales(loc);
    } catch (e) {
      setToast({ message: 'Failed to load attributes', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (message, error = false) => {
    setToast({ message, error });
    setTimeout(() => setToast(null), 3000);
  };

  const openNew = () => {
    setEditing(blankForm());
    setIsNew(true);
    setFormError(null);
  };

  const openEdit = async (row) => {
    try {
      const full = await getAttribute(row.code);
      setEditing({
        id: full.id,
        version: full.version,
        code: full.code,
        label: full.label,
        type: full.type,
        originalType: full.type,
        group_code: full.group_code || '',
        is_localizable: full.is_localizable,
        is_scopable: full.is_scopable,
        is_required_default: full.is_required_default,
        settings: full.settings || {},
        options: (full.options || []).map((o) => ({
          code: o.code,
          sort_order: o.sort_order,
          labels: o.labels || {},
          source_value: o.source_value || '',
          destination_value: o.destination_value || '',
        })),
      });
      setIsNew(false);
      setFormError(null);
    } catch (e) {
      flash('Failed to load attribute', true);
    }
  };

  const setField = (key, value) => setEditing((f) => ({ ...f, [key]: value }));
  const setSetting = (key, value) =>
    setEditing((f) => ({ ...f, settings: { ...f.settings, [key]: value } }));
  // Changing the type prunes settings that don't apply to the new type.
  const setType = (value) =>
    setEditing((f) => ({ ...f, type: value, settings: pruneSettings(f.settings, value) }));

  // Allowed target types for the Type select. On create, any type. On edit, the
  // loaded type plus its compatible conversions (read from the API descriptors,
  // never mirrored in JS) so the rule lives only in field_types.py.
  const typeOptions = useMemo(() => {
    if (!editing) return [];
    if (isNew) return fieldTypes.map((ft) => ft.type);
    const desc = fieldTypes.find((ft) => ft.type === editing.originalType);
    const convertible = (desc && desc.convertible_to) || [];
    return [editing.originalType, ...convertible];
  }, [editing, isNew, fieldTypes]);

  const save = async () => {
    if (!editing.code.trim() || !editing.label.trim()) {
      setFormError('Code and label are required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      let attrId = editing.id;
      if (isNew) {
        const created = await createAttribute({
          code: editing.code.trim(),
          label: editing.label.trim(),
          type: editing.type,
          group_code: editing.group_code || null,
          is_localizable: editing.is_localizable,
          is_scopable: editing.is_scopable,
          is_required_default: editing.is_required_default,
          settings: editing.settings,
        });
        attrId = created.id;
      } else {
        await updateAttribute(editing.id, editing.version, {
          label: editing.label.trim(),
          type: editing.type,
          group_code: editing.group_code || null,
          is_localizable: editing.is_localizable,
          is_scopable: editing.is_scopable,
          is_required_default: editing.is_required_default,
          settings: editing.settings,
        });
      }
      if (OPTION_TYPES.has(editing.type)) {
        await replaceAttributeOptions(attrId, editing.options.map((o, i) => {
          const labels = Object.fromEntries(
            Object.entries(o.labels || {}).filter(([, v]) => (v || '').trim()),
          );
          const opt = { code: o.code, sort_order: i, labels };
          // Empty Source/Destination are omitted; the backend normalizes to NULL.
          if ((o.source_value || '').trim()) opt.source_value = o.source_value.trim();
          if ((o.destination_value || '').trim()) opt.destination_value = o.destination_value.trim();
          return opt;
        }));
      }
      flash('Attribute saved.');
      setEditing(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setFormError(typeof detail === 'string' ? detail : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      const res = await deleteAttribute(confirmDelete.id);
      flash(`Deleted ${res.code}.`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || 'Delete failed', true);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      { key: 'code', header: 'Code', render: (r) => <code>{r.code}</code> },
      { key: 'label', header: 'Label' },
      { key: 'type', header: 'Type', render: (r) => <Badge variant="info">{r.type}</Badge> },
      { key: 'group_code', header: 'Group', render: (r) => r.group_code || '—' },
      {
        key: 'scope',
        header: 'Scope',
        render: (r) => (
          <>
            {r.is_localizable && <Badge variant="neutral">locale</Badge>}{' '}
            {r.is_scopable && <Badge variant="neutral">region</Badge>}
          </>
        ),
      },
      { key: 'option_count', header: 'Options', align: 'right', render: (r) => r.option_count || '—' },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <>
            <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
              Edit
            </Button>{' '}
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>
              Delete
            </Button>
          </>
        ),
      },
    ],
    [openEdit],
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button onClick={openNew}>+ New attribute</Button>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        empty={undefined}
      />

      {editing && (
        <Drawer
          title={isNew ? 'New attribute' : `Edit ${editing.code}`}
          sub={isNew
            ? 'Define a configurable field'
            : 'Code is immutable; type changes are limited to compatible field types'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save attribute'}
              </Button>
            </>
          }
        >
          {formError && <div className="pim-field-error" style={{ marginBottom: 12 }}>{formError}</div>}

          <div className="pim-row">
            <div className="pim-field">
              <label>Code</label>
              <input
                type="text"
                value={editing.code}
                disabled={!isNew}
                placeholder="lowercase_snake"
                onChange={(e) => setField('code', e.target.value)}
              />
            </div>
            <div className="pim-field">
              <label>Label</label>
              <input type="text" value={editing.label} onChange={(e) => setField('label', e.target.value)} />
            </div>
          </div>

          <div className="pim-row">
            <div className="pim-field">
              <label>Type</label>
              <select
                value={editing.type}
                disabled={!isNew && typeOptions.length <= 1}
                onChange={(e) => setType(e.target.value)}
              >
                {typeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {!isNew && editing.type !== editing.originalType && (
                <div className="pim-field-hint" style={{ color: '#b45309', marginTop: 4 }}>
                  Changing type re-normalizes every stored value for this attribute.
                  {editing.originalType === 'multiselect' && editing.type === 'simpleselect'
                    && ' Products holding more than one value must be reduced to one first.'}
                </div>
              )}
              {!isNew && typeOptions.length <= 1 && (
                <div className="pim-field-hint" style={{ marginTop: 4 }}>
                  This type has no compatible conversions.
                </div>
              )}
            </div>
            <div className="pim-field">
              <label>Group</label>
              <select value={editing.group_code} onChange={(e) => setField('group_code', e.target.value)}>
                <option value="">(none)</option>
                {groups.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pim-field">
            <label>Scope &amp; requirement</label>
            <div className="pim-checks">
              <label className="pim-check">
                <input
                  type="checkbox"
                  checked={editing.is_localizable}
                  onChange={(e) => setField('is_localizable', e.target.checked)}
                />
                Localizable (per locale)
              </label>
              <label className="pim-check">
                <input
                  type="checkbox"
                  checked={editing.is_scopable}
                  onChange={(e) => setField('is_scopable', e.target.checked)}
                />
                Scopable (per region)
              </label>
              <label className="pim-check">
                <input
                  type="checkbox"
                  checked={editing.is_required_default}
                  onChange={(e) => setField('is_required_default', e.target.checked)}
                />
                Required by default
              </label>
            </div>
          </div>

          <TypeSettings type={editing.type} settings={editing.settings} setSetting={setSetting} />

          {OPTION_TYPES.has(editing.type) && (
            <OptionsEditor
              options={editing.options}
              locales={locales}
              attributeCode={editing.code}
              canSeed={!isNew}
              flash={flash}
              onChange={(opts) => setField('options', opts)}
            />
          )}
        </Drawer>
      )}

      {confirmDelete && (
        <ConfirmModal
          title={`Delete ${confirmDelete.code}?`}
          body="This removes the attribute, its options, family links, and any stored values. This cannot be undone."
          danger
          confirmLabel="Delete"
          busy={saving}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}

function TypeSettings({ type, settings, setSetting }) {
  if (type === 'text' || type === 'textarea' || type === 'rich_text') {
    return (
      <div className="pim-row">
        <div className="pim-field">
          <label>Max length</label>
          <input
            type="number"
            value={settings.max_length ?? ''}
            onChange={(e) => setSetting('max_length', e.target.value ? Number(e.target.value) : undefined)}
          />
        </div>
        <div className="pim-field">
          <label>Validation pattern (regex)</label>
          <input
            type="text"
            value={settings.pattern ?? ''}
            onChange={(e) => setSetting('pattern', e.target.value || undefined)}
          />
        </div>
      </div>
    );
  }
  if (type === 'number') {
    return (
      <div className="pim-row">
        <div className="pim-field">
          <label>Min</label>
          <input type="number" value={settings.min ?? ''} onChange={(e) => setSetting('min', e.target.value ? Number(e.target.value) : undefined)} />
        </div>
        <div className="pim-field">
          <label>Max</label>
          <input type="number" value={settings.max ?? ''} onChange={(e) => setSetting('max', e.target.value ? Number(e.target.value) : undefined)} />
        </div>
      </div>
    );
  }
  if (type === 'currency') {
    return (
      <div className="pim-field">
        <label>Allowed currencies (comma-separated)</label>
        <input
          type="text"
          value={(settings.currencies || []).join(', ')}
          placeholder="AUD, NZD"
          onChange={(e) => setSetting('currencies', e.target.value.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean))}
        />
      </div>
    );
  }
  if (type === 'metric') {
    return (
      <div className="pim-row">
        <div className="pim-field">
          <label>Unit family</label>
          <input type="text" value={settings.unit_family ?? ''} placeholder="weight" onChange={(e) => setSetting('unit_family', e.target.value || undefined)} />
        </div>
        <div className="pim-field">
          <label>Allowed units (comma-separated)</label>
          <input
            type="text"
            value={(settings.units || []).join(', ')}
            placeholder="g, kg"
            onChange={(e) => setSetting('units', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          />
        </div>
      </div>
    );
  }
  if (type === 'image_carousel') {
    return (
      <div className="pim-field">
        <label>Max items</label>
        <input type="number" value={settings.max_items ?? ''} onChange={(e) => setSetting('max_items', e.target.value ? Number(e.target.value) : undefined)} />
      </div>
    );
  }
  if (OPTION_TYPES.has(type)) {
    return (
      <div className="pim-field">
        <label className="pim-check">
          <input
            type="checkbox"
            checked={!!settings.allow_arbitrary}
            onChange={(e) => setSetting('allow_arbitrary', e.target.checked || undefined)}
          />
          Allow arbitrary values (open vocabulary — inbound-mapped attributes)
        </label>
      </div>
    );
  }
  return null;
}

export function OptionsEditor({ options, locales, attributeCode, canSeed, flash, onChange }) {
  const localeCodes = (locales || []).map((l) => l.code);
  const [selectedLocale, setSelectedLocale] = useState(localeCodes[0] || '');
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedData, setSeedData] = useState(null); // { values, source_fields } or null when closed

  // Keep the selected locale valid as locales load in.
  useEffect(() => {
    if (!selectedLocale && localeCodes.length) setSelectedLocale(localeCodes[0]);
  }, [localeCodes, selectedLocale]);

  const add = () => onChange([...options, { code: '', labels: {}, source_value: '', destination_value: '' }]);
  const update = (i, key, value) =>
    onChange(options.map((o, idx) => (idx === i ? { ...o, [key]: value } : o)));
  const updateLabel = (i, value) =>
    onChange(options.map((o, idx) => (
      idx === i ? { ...o, labels: { ...(o.labels || {}), [selectedLocale]: value } } : o
    )));
  const remove = (i) => onChange(options.filter((_, idx) => idx !== i));

  // Lowercased source values already mapped, for the picker's pre-check / dedupe.
  const existingSourceSet = new Set(
    options.map((o) => (o.source_value || '').trim().toLowerCase()).filter(Boolean),
  );

  const openSeed = async () => {
    setSeedLoading(true);
    try {
      const data = await getAttributeSourceValues(attributeCode);
      if (data.reason === 'no_mapping') {
        flash('No source mapping configured for this attribute (set one under Mappings).', true);
        return;
      }
      if (data.reason === 'no_source_data' || !(data.values || []).length) {
        flash('The mapped source column has no values yet.', true);
        return;
      }
      setSeedData({ values: data.values, source_fields: data.source_fields || [] });
    } catch (e) {
      flash(e?.response?.data?.detail || 'Failed to load source values.', true);
    } finally {
      setSeedLoading(false);
    }
  };

  // Append picked raw values as new option rows. Dedupe by lower(source_value)
  // against existing options AND within the batch (the DB has a case-insensitive
  // unique index on source_value); resolve code collisions with a numeric suffix.
  const addSeeded = (selectedRaw) => {
    const usedCodes = new Set(
      options.map((o) => (o.code || '').trim().toLowerCase()).filter(Boolean),
    );
    const seenSource = new Set(existingSourceSet);
    const additions = [];
    selectedRaw.forEach((raw) => {
      const key = String(raw).trim().toLowerCase();
      if (!key || seenSource.has(key)) return;
      seenSource.add(key);
      const base = slugifyCode(raw);
      let candidate = base;
      let n = 2;
      while (usedCodes.has(candidate.toLowerCase())) {
        candidate = `${base}_${n}`;
        n += 1;
      }
      usedCodes.add(candidate.toLowerCase());
      additions.push({
        code: candidate,
        sort_order: 0, // Save re-assigns sort_order by index.
        labels: selectedLocale ? { [selectedLocale]: humanizeLabel(raw) } : {},
        source_value: String(raw),
        destination_value: '',
      });
    });
    if (additions.length) {
      onChange([...options, ...additions]);
      flash(`Added ${additions.length} option${additions.length === 1 ? '' : 's'} from source.`);
    }
    setSeedData(null);
  };

  return (
    <div className="pim-field">
      <div className="pim-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <label>Options &amp; value mappings</label>
        {localeCodes.length > 0 && (
          <div className="pim-field" style={{ maxWidth: 200 }}>
            <label>Label locale</label>
            <select value={selectedLocale} onChange={(e) => setSelectedLocale(e.target.value)}>
              {localeCodes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="pim-list-rows">
        <div className="pim-list-row pim-opt-head">
          <span className="grow">Source (Epicor)</span>
          <span className="grow">Code (PIM)</span>
          <span className="grow">Display label</span>
          <span className="grow">Destination (BigCommerce)</span>
          <span style={{ width: 64 }} />
        </div>
        {options.map((o, i) => (
          <div className="pim-list-row" key={i}>
            <input
              type="text"
              className="grow"
              placeholder="finished_good"
              value={o.source_value || ''}
              onChange={(e) => update(i, 'source_value', e.target.value)}
            />
            <input
              type="text"
              className="grow"
              placeholder="option_code"
              value={o.code}
              onChange={(e) => update(i, 'code', e.target.value)}
            />
            <input
              type="text"
              className="grow"
              placeholder={selectedLocale ? `Label (${selectedLocale})` : 'Label'}
              value={(o.labels || {})[selectedLocale] || ''}
              disabled={!selectedLocale}
              onChange={(e) => updateLabel(i, e.target.value)}
            />
            <input
              type="text"
              className="grow"
              placeholder="product"
              value={o.destination_value || ''}
              onChange={(e) => update(i, 'destination_value', e.target.value)}
            />
            <button type="button" className="pim-mini-btn" onClick={() => remove(i)}>
              Remove
            </button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" className="pim-mini-btn" onClick={add}>
          + Add option
        </button>
        {canSeed ? (
          <button type="button" className="pim-mini-btn" onClick={openSeed} disabled={seedLoading}>
            {seedLoading ? 'Loading…' : 'Seed from source'}
          </button>
        ) : (
          <span className="pim-field-hint">Save the attribute to seed options from source data.</span>
        )}
      </div>

      {seedData && (
        <SeedSourceModal
          values={seedData.values}
          sourceFields={seedData.source_fields}
          existingSourceSet={existingSourceSet}
          onAdd={addSeeded}
          onClose={() => setSeedData(null)}
        />
      )}
    </div>
  );
}

function SeedSourceModal({ values, sourceFields, existingSourceSet, onAdd, onClose }) {
  // Pre-check every value that isn't already mapped (case-insensitive).
  const [checked, setChecked] = useState(() => {
    const init = {};
    values.forEach((v) => {
      const key = String(v.value).trim().toLowerCase();
      init[key] = !existingSourceSet.has(key);
    });
    return init;
  });

  const keyOf = (v) => String(v.value).trim().toLowerCase();
  const toggle = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));
  const selectable = values.filter((v) => !existingSourceSet.has(keyOf(v)));
  const selectedCount = selectable.filter((v) => checked[keyOf(v)]).length;
  const allSelected = selectable.length > 0 && selectedCount === selectable.length;

  const toggleAll = () =>
    setChecked((c) => {
      const next = { ...c };
      selectable.forEach((v) => { next[keyOf(v)] = !allSelected; });
      return next;
    });

  const apply = () =>
    onAdd(values.filter((v) => !existingSourceSet.has(keyOf(v)) && checked[keyOf(v)]).map((v) => v.value));

  return (
    <div className="pim-modal-overlay" onMouseDown={onClose}>
      <div className="pim-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pim-modal-head">
          <div>
            <h3>Seed options from source</h3>
            {sourceFields?.length > 0 && (
              <div className="pim-field-hint">From {sourceFields.join(', ')} — counts are products.</div>
            )}
          </div>
          <button type="button" className="pim-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="pim-modal-body">
          <label className="pim-seed-row pim-seed-all">
            <input type="checkbox" checked={allSelected} disabled={!selectable.length} onChange={toggleAll} />
            <span className="pim-seed-value">Select all unmapped</span>
          </label>
          {values.map((v) => {
            const key = keyOf(v);
            const already = existingSourceSet.has(key);
            return (
              <label key={key} className={`pim-seed-row${already ? ' is-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={!already && !!checked[key]}
                  disabled={already}
                  onChange={() => toggle(key)}
                />
                <span className="pim-seed-value">{v.value}</span>
                <span className="pim-seed-count">{v.count}</span>
                {already && <span className="pim-seed-tag">already mapped</span>}
              </label>
            );
          })}
        </div>
        <div className="pim-modal-foot">
          <button type="button" className="pim-mini-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="pim-mini-btn primary" onClick={apply} disabled={!selectedCount}>
            Add selected ({selectedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
